/**
 * Установка приложения: системное окно Chromium и подсказка для iOS.
 *
 * Android/Chromium присылает `beforeinstallprompt`, когда приложение можно
 * поставить. Событие перехватываем при загрузке модуля, а не в компоненте:
 * оно приходит один раз и может опередить монтирование React. iOS такого
 * события не даёт — там показываем инструкцию «Поделиться → На экран
 * „Домой“» (см. `components/install`).
 *
 * Состояние одно на всё приложение: событие приходит одно, поэтому общий
 * модульный store, а не подписка в каждом компоненте.
 */

import { useSyncExternalStore } from 'react';

/** Chromium-событие установки; в стандартных типах DOM его до сих пор нет. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed'; readonly platform: string }>;
  prompt(): Promise<void>;
}

export interface InstallState {
  /** Браузер готов показать системное окно установки. */
  readonly canPrompt: boolean;
  /** Приложение уже открыто с домашнего экрана (standalone). */
  readonly installed: boolean;
  /** iPhone/iPad: системного окна нет, нужна инструкция. */
  readonly isIos: boolean;
}

const isBrowser = typeof window !== 'undefined';

function detectInstalled(): boolean {
  if (!isBrowser) return false;
  const nav = navigator as Navigator & { readonly standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

function detectIos(): boolean {
  if (!isBrowser) return false;
  // iPadOS 13+ представляется как Mac — отличаем по тач-экрану.
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = { canPrompt: false, installed: detectInstalled(), isIos: detectIos() };
const listeners = new Set<() => void>();

function emit(patch: Partial<InstallState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (isBrowser) {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Свой баннер вместо мини-подсказки браузера; событие покажем по кнопке.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit({ canPrompt: true });
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit({ canPrompt: false, installed: true });
  });
  window.matchMedia('(display-mode: standalone)').addEventListener('change', (event) => {
    if (event.matches) emit({ installed: true, canPrompt: false });
  });
}

/**
 * Показать системное окно установки. `unavailable` — событие не пришло
 * (браузер не умеет или установка уже начата); кнопку в этом случае
 * компоненты не показывают.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (event === null) return 'unavailable';
  // Событие одноразовое: после показа браузер пришлёт новое, если передумали.
  deferred = null;
  emit({ canPrompt: false });
  await event.prompt();
  const choice = await event.userChoice;
  if (choice.outcome === 'accepted') emit({ installed: true });
  return choice.outcome;
}

export function useInstallPrompt(): InstallState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Кнопку показываем, только когда установка возможна: iOS — с инструкцией. */
export function canShowInstall(state: InstallState): boolean {
  return !state.installed && (state.isIos || state.canPrompt);
}
